'use client'

import type { ViewMeta } from '@/constants/views'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ArrowsOutIcon, DeviceMobileIcon, DeviceTabletIcon, MonitorIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CodeBlock } from '@/components/docs/code-block'

type FrameWidth = 'desktop' | 'tablet' | 'phone'

const WIDTHS: { id: FrameWidth, label: string, icon: typeof MonitorIcon, width: number }[] = [
    { id: 'desktop', label: 'Desktop, 1280px and up', icon: MonitorIcon, width: 1280 },
    { id: 'tablet', label: 'Tablet, 768px', icon: DeviceTabletIcon, width: 768 },
    { id: 'phone', label: 'Phone, 390px', icon: DeviceMobileIcon, width: 390 }
]

/* The frame renders the page at its real width and scales it down to fit when the docs column is narrower. */
export function ScaledFrame({ id, title, height, width }: { id: string, title: string, height: number, width: number }) {
    const ref = useRef<HTMLDivElement>(null)
    const [available, setAvailable] = useState(0)

    useEffect(() => {
        const element = ref.current
        if (!element) return
        const observer = new ResizeObserver(([entry]) => setAvailable(Math.floor(entry.contentRect.width)))
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    const virtual = width === 1280 ? Math.max(available, 1280) : width
    const scale = available ? Math.min(1, available / virtual) : 1

    return (
        <div ref={ref} className='flex w-full justify-center overflow-hidden rounded-xl border border-border-default bg-surface-muted' style={{ height }}>
            {available > 0 && (
                <div style={{ width: virtual * scale, height }} className='shrink-0'>
                    <iframe
                        src={`/view/${id}`}
                        title={`${title} preview`}
                        loading='lazy'
                        className='origin-top-left border-0 bg-surface-page'
                        style={{ width: virtual, height: height / scale, transform: `scale(${scale})` }}
                    />
                </div>
            )}
        </div>
    )
}

interface BlockFrameProps {
    view: ViewMeta
    source?: string
}

/*
 * A block or template in a frame: Preview and Code switch, a desktop, tablet and phone width switch,
 * and a link to its own full-screen page. The preview is the real page at /view/<id>, so breakpoints respond for real.
 */
export function BlockFrame({ view, source }: BlockFrameProps) {
    const [width, setWidth] = useState<FrameWidth>('desktop')
    const current = WIDTHS.find(item => item.id === width) ?? WIDTHS[0]

    // The width switch is hidden on phones, so show the phone layout there.
    useEffect(() => {
        if (window.innerWidth < 768) setWidth('phone')
    }, [])

    return (
        <section id={view.id} aria-labelledby={`${view.id}-title`} className='flex scroll-mt-24 flex-col gap-4'>
            <Tabs defaultValue='preview' className='flex min-w-0 flex-col gap-4'>
                <div className='flex flex-wrap items-end justify-between gap-4'>
                    <div className='flex min-w-0 flex-col gap-1'>
                        <h2 id={`${view.id}-title`} className='flex flex-wrap items-center gap-2 font-heading text-lg font-semibold text-text-primary'>
                            <a href={`#${view.id}`} className='rounded-sm outline-none hover:underline focus-visible:outline-2 focus-visible:outline-border-focus'>
                                {view.title}
                            </a>
                            {view.dark && (
                                <Badge variant='secondary' className='font-sans'>
                                    Dark
                                </Badge>
                            )}
                        </h2>
                        <p className='max-w-2xl text-base text-text-secondary'>
                            {view.description}
                        </p>
                    </div>
                    <div className='flex flex-wrap items-center gap-2'>
                        <TabsList>
                            <TabsTrigger value='preview'>
                                Preview
                            </TabsTrigger>
                            <TabsTrigger value='code'>
                                Code
                            </TabsTrigger>
                        </TabsList>
                        <div role='group' aria-label='Preview width' className='hidden items-center gap-1 rounded-md bg-action-segment-track p-1 md:flex'>
                            {WIDTHS.map(item => (
                                <Tooltip key={item.id}>
                                    <TooltipTrigger
                                        render={(
                                            <button
                                                type='button'
                                                onClick={() => setWidth(item.id)}
                                                aria-pressed={width === item.id}
                                                aria-label={item.label}
                                                className={cn(
                                                    'inline-flex size-9 cursor-pointer items-center justify-center rounded-sm transition-colors outline-none focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-4',
                                                    width === item.id ? 'bg-action-segment-active text-action-segment-active-foreground' : 'text-action-segment-inactive-foreground hover:bg-surface-raised/60'
                                                )}
                                            />
                                        )}
                                    >
                                        <item.icon aria-hidden='true' />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {item.label}
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                        <Button variant='outline' size='sm' className='h-11' render={<Link href={`/view/${view.id}`} />} nativeButton={false} aria-label={`Open ${view.title} full screen`}>
                            <ArrowsOutIcon aria-hidden='true' />
                            <span className='hidden sm:inline'>
                                Full screen
                            </span>
                        </Button>
                    </div>
                </div>
                <TabsContent value='preview'>
                    <ScaledFrame id={view.id} title={view.title} height={view.height} width={current.width} />
                </TabsContent>
                <TabsContent value='code' className='flex flex-col gap-4'>
                    <CodeBlock title='Usage' code={view.usage} />
                    {source && <CodeBlock title={view.source} code={source} maxHeight={560} />}
                </TabsContent>
            </Tabs>
        </section>
    )
}
